import type { ReviewCreditEligibility, ReviewCreditSummary } from '../../model/review/review';

/** Only `eligible` promises money; every other state still says the review is welcome. */
export type ReviewCreditBannerStatus =
  | 'eligible'
  | 'guest'
  | 'notPurchased'
  | 'alreadyCredited'
  | 'nothingToClaim';

export interface ReviewCreditBannerCopy {
  headline: string;
  wideHeadline?: string;
  note?: string;
}

export interface ReviewCreditRange {
  text: string;
  media: string;
}

interface ProductReviewCreditStatusParams {
  isSignedIn: boolean;
  eligibility?: ReviewCreditEligibility;
}

interface OrdersReviewCreditStatusParams {
  isSignedIn: boolean;
  summary?: ReviewCreditSummary;
}

/** An unanswered call keeps the offer up: a network error must not tell a buyer they earn nothing. */
export const resolveProductReviewCreditStatus = ({
  isSignedIn,
  eligibility,
}: ProductReviewCreditStatusParams): ReviewCreditBannerStatus => {
  if (!isSignedIn) {
    return 'guest';
  }
  if (!eligibility || eligibility.eligible) {
    return 'eligible';
  }
  return eligibility.alreadyCredited ? 'alreadyCredited' : 'notPurchased';
};

export const resolveOrdersReviewCreditStatus = ({
  isSignedIn,
  summary,
}: OrdersReviewCreditStatusParams): ReviewCreditBannerStatus => {
  if (!isSignedIn) {
    return 'guest';
  }
  if (!summary) {
    return 'eligible';
  }
  return summary.claimable ? 'eligible' : 'nothingToClaim';
};

/** Shared by web and the app. Figures stay out of the copy until priced: a half range reads as less. */
export const buildReviewCreditBannerCopy = (
  status: ReviewCreditBannerStatus,
  credit?: ReviewCreditRange,
): ReviewCreditBannerCopy => {
  const membersEarn = credit ? `Members earn ${credit.text} – ${credit.media} per review. ` : '';

  if (status === 'eligible') {
    return { headline: 'Earn credits for your review!', wideHeadline: 'Earn credits for your reviews!' };
  }
  if (status === 'guest') {
    return {
      headline: 'Sign in to earn credits for your review!',
      note: `Only members who purchased and received this product earn credits. ${membersEarn}You can still post your review.`,
    };
  }
  if (status === 'alreadyCredited') {
    return {
      headline: 'You have already earned credit for this product.',
      note: 'Credit is paid once per purchase. You can still post your review.',
    };
  }
  if (status === 'nothingToClaim') {
    return {
      headline: 'No review credits to claim right now.',
      note: credit
        ? `Credit applies to delivered orders you have not reviewed yet — up to ${credit.media} per review.`
        : 'Credit applies to delivered orders you have not reviewed yet.',
    };
  }
  return {
    headline: 'Only members who purchased and received this product earn credits.',
    note: `${membersEarn}You can still post your review.`,
  };
};
